import { useCallback, useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'

import {
  getWorkGroup,
  updateWorkGroup,
  addUserToWorkGroup,
  removeUserFromWorkGroup,
  addApplicationToWorkGroup,
  removeApplicationFromWorkGroup,
} from '../../api/workGroups.js'
import { listUsers } from '../../api/users.js'
import { listApplications } from '../../api/applications.js'
import { useFeedback } from '../../components/ui/FeedbackProvider.jsx'

export default function WorkGroupDetailPage() {
  const { id } = useParams()
  const { toast, confirm } = useFeedback()
  const [wg, setWg] = useState(null)
  const [loading, setLoading] = useState(true)
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')

  const [allUsers, setAllUsers] = useState([])
  const [selectedUserId, setSelectedUserId] = useState('')
  const [allApplications, setAllApplications] = useState([])
  const [selectedApplicationId, setSelectedApplicationId] = useState('')

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const [data, users, applications] = await Promise.all([
        getWorkGroup(id),
        listUsers().catch(() => []),
        listApplications().catch(() => []),
      ])
      setWg(data)
      setTitle(data.title)
      setDescription(data.description || '')
      setAllUsers(users)
      setAllApplications(applications)
    } catch {
      toast({ type: 'error', message: 'Failed to load work group.' })
    } finally {
      setLoading(false)
    }
  }, [id, toast])

  useEffect(() => { load() }, [load])

  async function handleSave(e) {
    e.preventDefault()
    try {
      await updateWorkGroup(id, { title, description: description || null })
      toast({ type: 'success', message: 'Work group updated.' })
      load()
    } catch (err) {
      toast({ type: 'error', message: err?.body?.detail || 'Update failed.' })
    }
  }

  async function handleAddUser() {
    if (!selectedUserId) return
    try {
      await addUserToWorkGroup(id, Number(selectedUserId))
      toast({ type: 'success', message: 'User added.' })
      setSelectedUserId('')
      load()
    } catch (err) {
      toast({ type: 'error', message: err?.body?.detail || 'Failed to add user.' })
    }
  }

  async function handleRemoveUser(userId) {
    const ok = await confirm({ title: 'Remove user', message: 'Remove this user from the work group?' })
    if (!ok) return
    try {
      await removeUserFromWorkGroup(id, userId)
      toast({ type: 'success', message: 'User removed.' })
      load()
    } catch (err) {
      toast({ type: 'error', message: err?.body?.detail || 'Failed to remove user.' })
    }
  }

  async function handleAddApplication() {
    if (!selectedApplicationId) return
    try {
      await addApplicationToWorkGroup(id, selectedApplicationId)
      toast({ type: 'success', message: 'Application added.' })
      setSelectedApplicationId('')
      load()
    } catch (err) {
      toast({ type: 'error', message: err?.body?.detail || 'Failed to add application.' })
    }
  }

  async function handleRemoveApplication(applicationId) {
    const ok = await confirm({
      title: 'Remove application',
      message: 'Remove this application from the work group?',
    })
    if (!ok) return
    try {
      await removeApplicationFromWorkGroup(id, applicationId)
      toast({ type: 'success', message: 'Application removed.' })
      load()
    } catch (err) {
      toast({ type: 'error', message: err?.body?.detail || 'Failed to remove application.' })
    }
  }

  if (loading) return <div className="page"><div className="muted">Loading...</div></div>
  if (!wg) return <div className="page"><div className="muted">Work group not found.</div></div>

  const memberIds = new Set(wg.users.map((u) => u.id))
  const availableUsers = allUsers.filter((u) => !memberIds.has(u.id))
  const projectIds = new Set((wg.applications || []).map((a) => a.id))
  const availableApplications = allApplications.filter((a) => !projectIds.has(a.id))

  return (
    <div className="page">
      <div className="pageHeader">
        <h1 className="pageTitle">Work Group: {wg.title}</h1>
        <Link to="/work-groups" className="button buttonGhost">
          Back
        </Link>
      </div>

      <section className="card formCard">
        <div className="cardTitle">Work Group</div>
        <form className="formGrid" onSubmit={handleSave}>
          <label className="formField">
            <span className="formLabel">Title</span>
            <input
              className="textInput"
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              required
            />
          </label>
          <label className="formField">
            <span className="formLabel">Description</span>
            <textarea
              className="textInput"
              rows={3}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </label>
          <div className="formActions">
            <button type="submit" className="button buttonPrimary">
              Save
            </button>
          </div>
        </form>
      </section>

      <section className="card sectionCard" style={{ marginTop: 24 }}>
        <div className="pageHeader pageHeaderTight">
          <div className="cardTitle">Users ({wg.users.length})</div>
        </div>
        <div className="buttonGroup" style={{ marginBottom: 12 }}>
          <select
            className="textInput"
            value={selectedUserId}
            onChange={(e) => setSelectedUserId(e.target.value)}
            style={{ maxWidth: 300 }}
          >
            <option value="">Select user...</option>
            {availableUsers.map((u) => (
              <option key={u.id} value={u.id}>{u.display_name || u.email} ({u.role})</option>
            ))}
          </select>
          <button type="button" className="button buttonPrimary" onClick={handleAddUser} disabled={!selectedUserId}>
            Add
          </button>
        </div>
        {wg.users.length > 0 ? (
          <table className="table" aria-label="Work group users">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Role</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {wg.users.map((u) => (
                <tr key={u.id}>
                  <td>{u.display_name}</td>
                  <td>{u.email}</td>
                  <td>{u.role}</td>
                  <td>
                    <button
                      type="button"
                      className="button buttonGhost buttonDanger buttonSm"
                      onClick={() => handleRemoveUser(u.id)}
                    >
                      Remove
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p className="muted">No users assigned.</p>
        )}
      </section>

      <section className="card sectionCard" style={{ marginTop: 24 }}>
        <div className="pageHeader pageHeaderTight">
          <div className="cardTitle">Applications ({wg.applications?.length ?? 0})</div>
        </div>
        <div className="buttonGroup" style={{ marginBottom: 12 }}>
          <select
            className="textInput"
            value={selectedApplicationId}
            onChange={(e) => setSelectedApplicationId(e.target.value)}
            style={{ maxWidth: 300 }}
          >
            <option value="">Select application...</option>
            {availableApplications.map((app) => (
              <option key={app.id} value={app.id}>
                {app.name} ({app.platform})
              </option>
            ))}
          </select>
          <button
            type="button"
            className="button buttonPrimary"
            onClick={handleAddApplication}
            disabled={!selectedApplicationId}
          >
            Add
          </button>
        </div>
        {wg.applications?.length > 0 ? (
          <table className="table" aria-label="Work group applications">
            <thead>
              <tr>
                <th>Name</th>
                <th>Platform</th>
                <th>Bundle ID</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {wg.applications.map((app) => (
                <tr key={app.id}>
                  <td>{app.name}</td>
                  <td>{app.platform}</td>
                  <td>{app.bundle_id}</td>
                  <td>
                    <button
                      type="button"
                      className="button buttonGhost buttonDanger buttonSm"
                      onClick={() => handleRemoveApplication(app.id)}
                    >
                      Remove
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p className="muted">No applications assigned.</p>
        )}
      </section>
    </div>
  )
}
